import { useCallback, useEffect, useMemo, useState } from "react";
import { X, Loader2, AlertTriangle, Search, RotateCcw, Users2, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * iter159 — Edit Final Crew drawer.
 *
 * Post-shoot correction UI. Opens for completed bookings and lets admin
 * replace one crew member on one shoot_crew row with a mandatory
 * reason. Backend rebalances the ledger automatically and records the
 * change in `shoot_crew_replacement_audit` (read via the audit tab).
 */
const REASON_PRESETS = [
  "Last Minute Replacement",
  "Sick",
  "Emergency",
  "Client Request",
  "Wrong Assignment",
  "Other",
];

export default function EditFinalCrewDrawer({ booking, onClose, onChanged }) {
  const [crew, setCrew] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("crew"); // "crew" | "audit"
  const [pending, setPending] = useState(null); // { row, newStaffId, reason, hasPayment, confirmed }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [crewRes, staffRes, auditRes] = await Promise.all([
        api.get(`/team/bookings/${booking.id}/crew`),
        api.get("/team/staff"),
        api.get(`/team/bookings/${booking.id}/replace-crew/audit`),
      ]);
      setCrew(crewRes.data.rows || []);
      setStaffList(staffRes.data || []);
      setAudit(auditRes.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load crew");
    } finally { setLoading(false); }
  }, [booking.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !pending) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, pending]);

  const staffById = useMemo(() =>
    Object.fromEntries((staffList || []).map((s) => [s.id, s])),
  [staffList]);

  const confirm = async () => {
    if (!pending) return;
    const { row, newStaffId, reason, hasPayment, confirmed } = pending;
    if (!reason || reason.length < 2) {
      toast.error("Reason is required");
      return;
    }
    if (hasPayment && !confirmed) {
      // second click needed
      setPending({ ...pending, confirmed: true });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/team/bookings/${booking.id}/replace-crew`, {
        row_id: row.id,
        new_staff_id: newStaffId,
        reason,
        confirm_paid: hasPayment,
      });
      toast.success("Crew replaced · ledger rebalanced");
      setPending(null);
      onChanged?.();
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && typeof detail === "object" && detail.code === "payroll_already_paid") {
        setPending({ ...pending, hasPayment: true });
        toast.warning(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Replace failed");
      }
    } finally { setBusy(false); }
  };

  const inrFmt = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !pending) onClose?.(); }}
      data-testid="edit-final-crew-drawer"
    >
      <div className="w-full max-w-4xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Post-Shoot Corrections</div>
            <h2 className="font-heading text-xl font-semibold text-stone-900 flex items-center gap-2">
              <Users2 className="w-5 h-5 text-rose-700" strokeWidth={1.8} /> Edit Final Crew
            </h2>
            <div className="text-xs text-stone-500 mt-1">
              <span className="font-semibold text-stone-800">{booking.client_name}</span>
              {booking.event ? <span className="mx-1.5">· {booking.event}</span> : null}
              {booking.location ? <span className="mx-1.5">· {booking.location}</span> : null}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-stone-100" aria-label="Close" data-testid="edit-final-crew-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pt-3 border-b border-stone-100 sticky top-[73px] bg-white z-10 flex gap-4">
          {["crew", "audit"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`text-xs font-semibold uppercase tracking-widest pb-2 border-b-2 ${tab === t ? "border-rose-700 text-rose-800" : "border-transparent text-stone-500 hover:text-stone-800"}`}
              data-testid={`edit-final-crew-tab-${t}`}
            >
              {t === "crew" ? "Current Crew" : `Audit History (${audit.length})`}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center text-stone-400 py-10"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : tab === "crew" ? (
            crew.length === 0 ? (
              <div className="text-center text-stone-400 py-16" data-testid="edit-final-crew-empty">
                No crew rows on this booking.
              </div>
            ) : (
              <div className="rounded-xl border border-stone-200 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                    <tr className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold text-left">
                      <th className="px-3 py-2">Current Staff</th>
                      <th className="px-3 py-2 w-24">Role</th>
                      <th className="px-3 py-2 w-28">Event Date</th>
                      <th className="px-3 py-2 w-28 text-right">Amount</th>
                      <th className="px-3 py-2 w-56">Replace</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {crew.map((row) => (
                      <tr key={row.id} data-testid={`edit-final-crew-row-${row.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-stone-800">{row.staff_name || "—"}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {row.payroll_frozen ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-widest font-bold">
                                Payroll Live
                              </span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600 uppercase tracking-widest font-bold">
                                Planning
                              </span>
                            )}
                            {row.has_payment && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-widest font-bold">
                                <IndianRupee className="w-2.5 h-2.5" /> Paid
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs uppercase tracking-wider text-stone-600">{row.job_role || "—"}</td>
                        <td className="px-3 py-2 font-mono text-xs text-stone-600">{row.event_date || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{inrFmt(row.actual_rate)}</td>
                        <td className="px-3 py-2">
                          <ReplacePicker
                            row={row}
                            staffList={staffList}
                            onPick={(newStaffId) => setPending({
                              row, newStaffId, reason: "Last Minute Replacement",
                              hasPayment: row.has_payment, confirmed: false,
                            })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <AuditList audit={audit} inrFmt={inrFmt} />
          )}
        </div>

        {pending && (
          <ConfirmReplaceModal
            pending={pending}
            staffById={staffById}
            busy={busy}
            inrFmt={inrFmt}
            onCancel={() => setPending(null)}
            onSubmit={confirm}
            onChange={(patch) => setPending((p) => ({ ...p, ...patch }))}
          />
        )}
      </div>
    </div>
  );
}

function ReplacePicker({ row, staffList, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (staffList || []).filter((s) =>
      s.id !== row.staff_id && (!t || (s.name || "").toLowerCase().includes(t)),
    ).slice(0, 20);
  }, [staffList, q, row.staff_id]);
  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-400" />
        <input
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search & replace…"
          className="input text-xs pl-6 w-full"
          data-testid={`edit-final-crew-search-${row.id}`}
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-stone-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((s) => (
            <button
              key={s.id}
              type="button"
              onMouseDown={() => { onPick(s.id); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-rose-50"
              data-testid={`edit-final-crew-pick-${row.id}-${s.id}`}
            >
              <span className="font-medium text-stone-800">{s.name}</span>
              <span className="text-stone-500 ml-2">₹{Math.round(s.base_shoot_rate || 3000).toLocaleString("en-IN")} / shoot</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfirmReplaceModal({ pending, staffById, busy, inrFmt, onCancel, onSubmit, onChange }) {
  const { row, newStaffId, reason, hasPayment, confirmed } = pending;
  const newStaff = staffById[newStaffId];
  const newRate = newStaff?.base_shoot_rate ? Number(newStaff.base_shoot_rate) : 3000;
  const oldAmount = Number(row.actual_rate || 0);
  // Preserve override_amount on the row; if none, ledger will re-price to new staff's default.
  const newAmount = row.override_amount != null ? Number(row.override_amount) : newRate;
  return (
    <div className="absolute inset-0 z-40 bg-black/40 flex items-center justify-center p-6" data-testid="edit-final-crew-confirm">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center gap-2">
          {hasPayment && <AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={2} />}
          <h3 className="font-heading text-lg font-semibold text-stone-900">
            Confirm Crew Replacement
          </h3>
        </div>
        {hasPayment && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="edit-final-crew-warning">
            <div className="font-bold mb-1">This crew member has already received payment.</div>
            Replacing them will update payroll and outstanding balances. Continue only if you're certain.
          </div>
        )}
        <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs space-y-2 font-mono">
          <div className="flex justify-between">
            <span className="text-stone-500 uppercase tracking-widest">Remove</span>
            <span>{row.staff_name} · {inrFmt(oldAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500 uppercase tracking-widest">Add</span>
            <span className="text-emerald-800 font-semibold">{newStaff?.name || "—"} · {inrFmt(newAmount)}</span>
          </div>
          <div className="border-t border-stone-200 pt-2 flex justify-between">
            <span className="text-stone-500 uppercase tracking-widest">Net Ledger Δ</span>
            <span className={newAmount - oldAmount >= 0 ? "text-emerald-800 font-semibold" : "text-red-700 font-semibold"}>
              {newAmount - oldAmount >= 0 ? "+" : ""}{inrFmt(newAmount - oldAmount)}
            </span>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold block mb-1">
            Reason (required)
          </label>
          <select
            value={REASON_PRESETS.includes(reason) ? reason : "Other"}
            onChange={(e) => onChange({ reason: e.target.value === "Other" ? "" : e.target.value })}
            className="input w-full text-sm mb-2"
            data-testid="edit-final-crew-reason-preset"
          >
            {REASON_PRESETS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {(!REASON_PRESETS.includes(reason) || reason === "Other") && (
            <input
              type="text"
              value={reason}
              onChange={(e) => onChange({ reason: e.target.value })}
              placeholder="Describe the reason…"
              className="input w-full text-sm"
              maxLength={280}
              data-testid="edit-final-crew-reason-custom"
            />
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={busy}
                  className="btn btn-outline btn-sm"
                  data-testid="edit-final-crew-cancel">
            Cancel
          </button>
          <button type="button" onClick={onSubmit} disabled={busy || !reason || reason.length < 2}
                  className={`btn btn-sm inline-flex items-center gap-1 ${hasPayment && !confirmed ? "bg-amber-700 text-white hover:bg-amber-800" : "btn-primary"}`}
                  data-testid="edit-final-crew-confirm-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {hasPayment && !confirmed ? "I understand — Continue" : "Replace Crew"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditList({ audit, inrFmt }) {
  if (audit.length === 0) {
    return (
      <div className="text-center text-stone-400 py-16" data-testid="edit-final-crew-audit-empty">
        <RotateCcw className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.2} />
        <div className="font-heading text-lg text-stone-500 mb-1">No replacements yet</div>
        <div className="text-xs">Every crew change on this booking will be permanently logged here.</div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {audit.map((a) => (
        <div key={a.id} className="rounded-lg border border-stone-200 p-3 text-xs" data-testid={`edit-final-crew-audit-${a.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <div className="font-semibold text-stone-800">
                {a.old_staff_name || "—"} <span className="text-stone-400 mx-1">→</span> {a.new_staff_name || "—"}
              </div>
              <div className="text-stone-500 mt-0.5">
                {a.reason} · {a.job_role} · {a.event_date}
              </div>
              <div className="text-[10px] text-stone-400 mt-0.5">
                {a.changed_by_email} · {(a.at || "").replace("T", " ").slice(0, 19)}
              </div>
            </div>
            <div className="text-right font-mono">
              <div className="text-red-700 line-through">{inrFmt(a.old_amount)}</div>
              <div className="text-emerald-800 font-semibold">{inrFmt(a.new_amount)}</div>
              {a.had_prior_payment && (
                <div className="text-[10px] text-amber-800 uppercase tracking-widest font-bold mt-1">
                  Had prior payment
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
